<template>
  <div>
    <div class="row g-2 mb-3">
      <div class="col-auto">
        <input
          type="text"
          class="form-control form-control-sm"
          style="width: 260px"
          placeholder="Buscar por nome, e-mail ou id..."
          v-model="filters.search"
          @input="scheduleSearch"
        />
      </div>
      <div class="col-auto">
        <select
          class="form-select form-select-sm"
          v-model="filters.answered"
          @change="applyFilters"
        >
          <option value="">Respondeu: todos</option>
          <option value="1">Respondeu: sim</option>
          <option value="0">Respondeu: não</option>
        </select>
      </div>
      <div class="col-auto">
        <select
          class="form-select form-select-sm"
          v-model="filters.email_status"
          @change="applyFilters"
        >
          <option value="">Status do e-mail: todos</option>
          <option v-for="s in emailStatuses" :key="s" :value="s">
            {{ emailStatusLabel(s) }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm"></div>
    </div>
    <EmptyState v-else-if="!items.length" :title="'Sem destinatários'" :msg="' '" />
    <div v-else class="table-responsive">
      <table class="table table-sm table-hover mb-0">
        <thead>
          <tr>
            <th>Nome</th>
            <th>Tipo</th>
            <th>Status do e-mail</th>
            <th>Respondeu</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in items" :key="r.id">
            <td>
              <template v-if="r.user">
                {{ r.user.name }}
                <router-link :to="userDetailPath(r.user)" target="_blank">
                  <i class="fa-solid fa-arrow-up-right-from-square ms-1"></i>
                </router-link>
                <button
                  type="button"
                  class="btn btn-link btn-sm p-0 ms-1"
                  title="Buscar link da pesquisa deste destinatário"
                  :disabled="loadingLinkFor === r.user.id"
                  @click="toggleUserLink(r.user.id)"
                >
                  <span
                    v-if="loadingLinkFor === r.user.id"
                    class="spinner-border spinner-border-sm"
                  ></span>
                  <i v-else class="fa-solid fa-link"></i>
                </button>
                <div v-if="userLinks[r.user.id]" class="input-group input-group-sm mt-1" style="max-width: 320px">
                  <input
                    type="text"
                    class="form-control bg-light"
                    :ref="`link-${r.user.id}`"
                    :value="userLinks[r.user.id]"
                    readonly
                    @click="$event.target.select()"
                  />
                  <button class="btn btn-primary" @click="copyUserLink(r.user.id)" title="Copiar">
                    <i class="fa-solid fa-copy"></i>
                  </button>
                </div>
              </template>
              <template v-else>Conta removida</template>
              <div class="text-muted small">{{ r.user?.email }}</div>
            </td>
            <td>{{ r.user?.type === "psychologist" ? "Psicólogo" : "Paciente" }}</td>
            <td>
              <span
                v-if="r.email_status"
                class="badge"
                :class="emailStatusClass(r.email_status)"
                :title="(r.email_history || '').split('|').join(' → ')"
              >
                {{ emailStatusLabel(r.email_status) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span v-if="r.answered" class="text-success">
                <i class="fa-solid fa-check me-1"></i>{{ formatDate(r.answered_at) }}
              </span>
              <span v-else class="text-muted">Não</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex justify-content-between align-items-center mt-3" v-if="lastPage > 1">
      <span class="text-muted small">Mostrando {{ items.length }} de {{ total }}</span>
      <div class="d-flex gap-2">
        <button
          class="btn btn-sm btn-outline-secondary"
          :disabled="currentPage <= 1 || loading"
          @click="changePage(currentPage - 1)"
        >
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <span class="align-self-center small">Página {{ currentPage }} de {{ lastPage }}</span>
        <button
          class="btn btn-sm btn-outline-secondary"
          :disabled="currentPage >= lastPage || loading"
          @click="changePage(currentPage + 1)"
        >
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Enum fixo do webhook do SendGrid — não muda com frequência, sem necessidade
// de endpoint próprio pra popular esse dropdown.
const EMAIL_STATUSES = ["processed", "delivered", "open", "click", "bounce", "dropped", "spamreport"];

const EMAIL_STATUS_LABELS = {
  processed: "Aceito pelo SendGrid",
  delivered: "Entregue",
  open: "Aberto",
  click: "Clicou no link",
  bounce: "Não entregue",
  dropped: "Não entregue",
  spamreport: "Marcado como spam",
};

export default {
  name: "SurveyRecipients",
  props: {
    uuid: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      items: [],
      currentPage: 1,
      lastPage: 1,
      total: 0,
      emailStatuses: EMAIL_STATUSES,
      filters: { answered: "", email_status: "", search: "" },
      searchTimer: null,
      loadingLinkFor: null,
      userLinks: {},
    };
  },
  mounted() {
    this.fetchRecipients();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchRecipients() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, per_page: 50 };
        if (this.filters.answered !== "") params.answered = this.filters.answered;
        if (this.filters.email_status) params.email_status = this.filters.email_status;
        if (this.filters.search.trim()) params.search = this.filters.search.trim();
        const res = await this.$surveyAdminService.recipients(this.uuid, params);
        const page = res.data || {};
        this.items = page.items || [];
        this.currentPage = page.meta?.page || 1;
        this.lastPage = page.meta?.last_page || 1;
        this.total = page.meta?.total || 0;
      } catch (e) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao carregar destinatários.",
        });
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchRecipients();
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applyFilters(), 400);
    },
    changePage(page) {
      if (page < 1 || page > this.lastPage) return;
      this.currentPage = page;
      this.fetchRecipients();
    },
    // Busca (async) e cópia (sync) ficam separadas de propósito: um
    // clipboard.writeText chamado depois de um await perde a "ativação do
    // usuário" em vários navegadores e falha calado, sem copiar nada.
    async toggleUserLink(userId) {
      if (this.userLinks[userId]) {
        this.$delete(this.userLinks, userId);
        return;
      }
      this.loadingLinkFor = userId;
      try {
        const res = await this.$surveyAdminService.userLink(this.uuid, userId);
        this.$set(this.userLinks, userId, res.data.answer_url);
      } catch (e) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao buscar o link da pesquisa.",
        });
      } finally {
        this.loadingLinkFor = null;
      }
    },
    copyUserLink(userId) {
      const url = this.userLinks[userId];
      // Dentro de v-for, $refs[key] sempre vem como array no Vue 2 — mesmo
      // com nome de ref único por item.
      const refs = this.$refs[`link-${userId}`];
      const input = Array.isArray(refs) ? refs[0] : refs;

      // execCommand funciona em cima do <input> já selecionado, sem passar
      // pela Clipboard API assíncrona (que nesse ambiente estava falhando
      // calada e deixando o clipboard antigo intocado).
      let copied = false;
      if (input) {
        input.focus();
        input.select();
        try {
          copied = document.execCommand("copy");
        } catch (e) {
          copied = false;
        }
      }

      if (copied) {
        this.$eventBus.$emit("showToast", {
          title: "Copiado",
          msg: "Link da pesquisa copiado para a área de transferência.",
        });
        return;
      }

      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(
          () => {
            this.$eventBus.$emit("showToast", {
              title: "Copiado",
              msg: "Link da pesquisa copiado para a área de transferência.",
            });
          },
          () => {
            this.$eventBus.$emit("showToast", {
              title: "Não foi possível copiar automaticamente",
              msg: "O link já está selecionado no campo — use Ctrl/Cmd+C para copiar.",
            });
          }
        );
      } else {
        this.$eventBus.$emit("showToast", {
          title: "Copie manualmente",
          msg: "O link já está selecionado no campo — use Ctrl/Cmd+C para copiar.",
        });
      }
    },
    userDetailPath(user) {
      const base =
        user.type === "psychologist"
          ? this.$paths.psychologists.detail
          : this.$paths.users.detail;
      return `${base}/${user.id}`;
    },
    emailStatusLabel(status) {
      return EMAIL_STATUS_LABELS[status] || status;
    },
    emailStatusClass(status) {
      if (["delivered", "open", "click"].includes(status)) return "bg-success";
      if (["bounce", "dropped", "spamreport"].includes(status)) return "bg-danger";
      return "bg-secondary";
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD/MM/YYYY HH:mm") : "-";
    },
  },
};
</script>
